// Icon mapping for categories
import {
  TrendingUp, TrendingDown, Wallet, LayoutGrid, ArrowRightLeft,
  PieChart, Settings, Utensils, Car, Zap, Ticket, HeartPulse,
  ShoppingBag, MoreHorizontal, Laptop, Home, GraduationCap,
  Plane, Gift, DollarSign, CreditCard, Banknote, Building,
  Coffee, Smartphone, Wifi, Dumbbell, Music, BookOpen, Briefcase,
  Facebook, Instagram, Music2, Search, MessageCircle, Users, Code2, HelpCircle,
  PiggyBank, Target, Heart, Umbrella, Gem, Baby,
  Bitcoin, ShoppingCart, Fuel, Package, Receipt, Lightbulb, Shirt, PawPrint, LineChart, HandCoins, Landmark, Pill, Store, Coins, Sofa, Wrench, Tv, ArrowLeftRight, Gamepad2, Beer, Scissors
} from 'lucide-react';

export const iconMap = {
  TrendingUp, TrendingDown, Wallet, LayoutGrid, ArrowRightLeft,
  PieChart, Settings, Utensils, Car, Zap, Ticket, HeartPulse,
  ShoppingBag, MoreHorizontal, Laptop, Home, GraduationCap,
  Plane, Gift, DollarSign, CreditCard, Banknote, Building,
  Coffee, Smartphone, Wifi, Dumbbell, Music, BookOpen, Briefcase,
  PiggyBank, Target, Heart, Umbrella, Gem, Baby,
  Bitcoin, ShoppingCart, Fuel, Package, Receipt, Lightbulb, Shirt, PawPrint, LineChart, HandCoins, Landmark, Pill, Store, Coins, Sofa, Wrench, Tv, ArrowLeftRight, Gamepad2, Beer, Scissors
};

// Shared across onboarding (new users) and the one-time existing-user modal.
export const acquisitionSources = [
  { value: 'facebook', label: 'Facebook', icon: Facebook },
  { value: 'instagram', label: 'Instagram', icon: Instagram },
  { value: 'tiktok', label: 'TikTok', icon: Music2 },
  { value: 'google', label: 'Google', icon: Search },
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { value: 'friend', label: 'Un amigo', icon: Users },
  { value: 'programming_community', label: 'Comunidad de programadores', icon: Code2 },
  { value: 'other', label: 'Otro', icon: HelpCircle },
];

export const getIcon = (name) => iconMap[name] || MoreHorizontal;

// Category (and goal) colors appear only on the icon (redesign.md, Fase 2):
// the icon in the category's color over a low-opacity tint of it. Colors are
// stored as the dark-mode tone; in light mode --cat-strength mixes them toward
// black (≈ the Tailwind 600 shade) so they keep contrast on the lavender bg.
const CAT_FALLBACK = '#94a3b8';
export const catColor = (color) => `color-mix(in srgb, ${color || CAT_FALLBACK} var(--cat-strength), black)`;
export const catTint = (color, percent = 12) =>
  `color-mix(in srgb, ${color || CAT_FALLBACK} ${percent}%, transparent)`;

export const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('es-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

// Same as formatCurrency, but returns a masked placeholder when the global
// "hide amounts" toggle (see context/AmountsVisibilityContext.js) is on.
// A fixed-width placeholder rather than dot-per-digit — simpler, and it
// doesn't leak the number's magnitude either.
export const formatAmount = (amount, hidden, currency = 'USD') =>
  hidden ? '••••' : formatCurrency(amount, currency);

export const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('es-ES', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  }).format(date);
};

export const formatShortDate = (dateStr) => {
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('es-ES', {
    day: 'numeric',
    month: 'short'
  }).format(date);
};

// "Hoy, 2:40pm" / "Ayer, 9:15am" / "Martes, 6:02pm" / falls back to
// formatShortDate ("12 ago") past a week. Transaction timestamps do carry a
// real time component (backend defaults to datetime.now() on the ones that
// don't set one explicitly), so this is meaningful, not just decorative.
export const formatRelativeTime = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const time = new Intl.DateTimeFormat('es-ES', { hour: 'numeric', minute: '2-digit', hour12: true }).format(date);

  if (date.toDateString() === now.toDateString()) return `Hoy, ${time}`;

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return `Ayer, ${time}`;

  const diffDays = Math.floor((now - date) / 86400000);
  if (diffDays >= 0 && diffDays < 7) {
    const weekday = new Intl.DateTimeFormat('es-ES', { weekday: 'long' }).format(date);
    return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}, ${time}`;
  }

  return formatShortDate(dateStr);
};

// Day-level label for a transaction-group header (a whole day, not a single
// timestamp) — "Hoy" / "Ayer" / weekday+date, matching formatRelativeTime's
// near-term wording but without a time-of-day component.
// Compact day for list rows: "Hoy" / "Ayer" / "25 oct" (with the year only
// when it isn't the current one).
export const formatShortDay = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) return 'Hoy';
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return 'Ayer';
  return new Intl.DateTimeFormat('es-ES', {
    day: 'numeric', month: 'short', ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  }).format(date).replace('.', '');
};

export const formatDayLabel = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) return 'Hoy';

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return 'Ayer';

  const sameYear = date.getFullYear() === now.getFullYear();
  const weekday = new Intl.DateTimeFormat('es-ES', { weekday: 'long' }).format(date);
  const day = new Intl.DateTimeFormat('es-ES', { day: 'numeric', month: 'long', ...(sameYear ? {} : { year: 'numeric' }) }).format(date);
  return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}, ${day}`;
};

export const categoryColors = [
  '#10b981', '#8b5cf6', '#f43f5e', '#f59e0b', '#3b82f6',
  '#ec4899', '#06b6d4', '#22c55e', '#ef4444', '#f97316',
  '#a855f7', '#14b8a6', '#e11d48', '#eab308', '#6366f1'
];

// Category icon picker (redesign.md, Fase 5) — wide enough that every
// category can have its own: no more Efectivo/BTC/Provisiones all on ⚡.
// Keep in sync with ICON_POOL in backend/categorize.py.
export const categoryIcons = [
  'Utensils', 'ShoppingCart', 'Car', 'Fuel', 'Home', 'Lightbulb', 'Wifi', 'Smartphone',
  'HeartPulse', 'Pill', 'GraduationCap', 'Ticket', 'Gamepad2', 'Music', 'Coffee', 'Beer',
  'Shirt', 'Scissors', 'PawPrint', 'Baby', 'Gift', 'Plane', 'Dumbbell', 'BookOpen',
  'Banknote', 'Bitcoin', 'Coins', 'CreditCard', 'Wallet', 'PiggyBank', 'Package', 'Receipt',
  'Store', 'Wrench', 'Sofa', 'Tv', 'Briefcase', 'Laptop', 'LineChart', 'HandCoins',
  'ArrowLeftRight', 'Building', 'Landmark', 'ShoppingBag', 'Zap', 'TrendingUp', 'MoreHorizontal',
];

export const goalIcons = [
  'PiggyBank', 'Target', 'Home', 'Plane', 'Car', 'GraduationCap',
  'Heart', 'Gem', 'Baby', 'Umbrella', 'Gift', 'Laptop'
];

export const goalPeriods = [
  { value: 'daily', label: 'Diario', per: 'día' },
  { value: 'weekly', label: 'Semanal', per: 'semana' },
  { value: 'biweekly', label: 'Quincenal', per: 'quincena' },
  { value: 'monthly', label: 'Mensual', per: 'mes' },
];

export const defaultExpenseCategories = [
  { name: 'Alimentación', icon: 'Utensils', color: '#f59e0b' },
  { name: 'Transporte', icon: 'Car', color: '#3b82f6' },
  { name: 'Servicios', icon: 'Zap', color: '#8b5cf6' },
  { name: 'Entretenimiento', icon: 'Ticket', color: '#ec4899' },
  { name: 'Salud', icon: 'HeartPulse', color: '#ef4444' },
  { name: 'Compras', icon: 'ShoppingBag', color: '#f97316' }
];

export const defaultIncomeCategories = [
  { name: 'Salario', icon: 'TrendingUp', color: '#10b981' },
  { name: 'Freelance', icon: 'Laptop', color: '#06b6d4' },
  { name: 'Inversiones', icon: 'LineChart', color: '#22c55e' }
];
